import React, { useState } from 'react';
import { captureJudgmentSignal } from '../../lib/decisionIntelligenceService';
import { ALL_JUDGMENT_TYPES, JUDGMENT_TYPES } from '../../lib/judgment/judgmentTaxonomy';
import { SearchableItemPicker } from '../prioritization/SearchableItemPicker';
import type {
  CaptureSignalInput,
  DecisionRecommendation,
  JudgmentDomain,
  SignalJudgmentType,
} from '../../types/decisionIntelligence';

interface Props {
  workspaceId: string;
  itemId?: string;
  itemTitle?: string;
  sessionId?: string;
  onCaptured?: (signalId: string) => void;
  onCancel?: () => void;
}

const RECOMMENDATIONS: { value: DecisionRecommendation; label: string; description: string }[] = [
  { value: 'proceed',  label: 'Proceed',  description: 'Move forward as planned' },
  { value: 'modify',   label: 'Modify',   description: 'Proceed with changes'     },
  { value: 'pause',    label: 'Pause',    description: 'Wait for more information' },
  { value: 'stop',     label: 'Stop',     description: 'Do not proceed'           },
];

const DOMAINS: JudgmentDomain[] = [
  'product','strategy','operations','hiring','architecture','gtm','resource','technical','general',
];

export function DecisionSignalCapture({ workspaceId, itemId, itemTitle, sessionId, onCaptured, onCancel }: Props) {
  // A signal is scored by the outcome logged on its item, so it needs one.
  const [pickedItemId, setPickedItemId] = useState<string | undefined>(undefined);
  const chosenItemId = itemId ?? pickedItemId;
  const [confidence, setConfidence] = useState<number>(7);
  const [recommendation, setRecommendation] = useState<DecisionRecommendation>('proceed');
  const [rationale, setRationale] = useState('');
  const [expectedOutcome, setExpectedOutcome] = useState('');
  const [expectedDays, setExpectedDays] = useState('');
  const [riskFlags, setRiskFlags] = useState('');
  const [domain, setDomain] = useState<JudgmentDomain>('general');
  const [judgmentType, setJudgmentType] = useState<SignalJudgmentType>('outcome_prediction');
  const [stakes, setStakes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    const input: CaptureSignalInput = {
      confidenceScore: confidence,
      recommendation,
      rationale:       rationale.trim() || undefined,
      expectedOutcome: expectedOutcome.trim() || undefined,
      expectedTimelineDays: expectedDays ? parseInt(expectedDays, 10) : undefined,
      riskFlags: riskFlags.split('\n').map(s => s.trim()).filter(Boolean),
      domain,
      judgmentType,
      stakes: stakes ? Number(stakes) : undefined,
    };

    try {
      const { signalId } = await captureJudgmentSignal(workspaceId, input, chosenItemId, sessionId);
      onCaptured?.(signalId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to capture signal.');
    } finally {
      setSaving(false);
    }
  }

  const confidenceColor =
    confidence >= 8 ? 'text-[var(--green)] dark:text-[var(--text-ter)]' :
    confidence >= 5 ? 'text-[var(--mustard)] dark:text-[var(--mustard)]'     :
                      'text-[var(--coral)] dark:text-[var(--coral)]';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {itemId ? (
        itemTitle && (
          <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)] truncate">
            Decision signal for: <span className="text-[var(--text-pri)] dark:text-[var(--text-pri)] font-medium">{itemTitle}</span>
          </p>
        )
      ) : (
        <SearchableItemPicker
          value={pickedItemId}
          onChange={setPickedItemId}
          label="Which item is this about?"
          helperText="An outcome logged on this item later scores this signal."
          emptyLabel="Choose an item"
          allowEmpty={false}
          required
        />
      )}

      {/* Confidence slider */}
      <div>
        <div className="flex justify-between items-center mb-1.5">
          <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)]">Confidence</label>
          <span className={`text-lg font-bold tabular-nums ${confidenceColor}`}>{confidence}<span className="text-xs text-[var(--text-ter)] dark:text-[var(--text-sec)]">/10</span></span>
        </div>
        <input
          type="range" min={1} max={10} value={confidence}
          onChange={e => setConfidence(Number(e.target.value))}
          className="w-full accent-violet-500 cursor-pointer"
        />
        <div className="flex justify-between text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] mt-0.5">
          <span>Very uncertain</span><span>Highly confident</span>
        </div>
      </div>

      {/* Recommendation */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-2">Recommendation</label>
        <div className="grid grid-cols-2 gap-2">
          {RECOMMENDATIONS.map(r => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRecommendation(r.value)}
              className={`text-left px-3 py-2  border text-xs transition-colors ${
                recommendation === r.value
                  ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)] dark:border-[var(--slate)] dark:bg-[var(--slate)]/30'
                  : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-ter)] hover:border-[var(--rule)] dark:border-[var(--rule)] dark:bg-[var(--surface-raised)]/50 dark:text-[var(--text-sec)] dark:hover:border-[var(--text-ter)]'
              }`}
            >
              <div className="font-semibold">{r.label}</div>
              <div className="text-[10px] opacity-70 mt-0.5">{r.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Kind of call (Judgment Analytics, mig 606) */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-2">Kind of call</label>
        <div className="flex flex-wrap gap-1.5">
          {ALL_JUDGMENT_TYPES.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => setJudgmentType(t as SignalJudgmentType)}
              title={JUDGMENT_TYPES[t].description}
              className={`px-2.5 py-1 border text-xs transition-colors ${
                judgmentType === t
                  ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                  : 'border-[var(--rule)] bg-[var(--surface)] text-[var(--text-ter)] hover:text-[var(--text-sec)]'
              }`}
            >
              {JUDGMENT_TYPES[t].label}
            </button>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-[var(--text-ter)]">{JUDGMENT_TYPES[judgmentType].description}</p>
      </div>

      {/* Stakes */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">How much this call matters <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">(1–10, optional)</span></label>
        <input
          type="number"
          min={1}
          max={10}
          value={stakes}
          onChange={e => setStakes(e.target.value)}
          placeholder="5"
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)]"
        />
      </div>

      {/* Domain */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Domain</label>
        <select
          value={domain}
          onChange={e => setDomain(e.target.value as JudgmentDomain)}
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] focus:outline-none focus:border-[var(--slate)]"
        >
          {DOMAINS.map(d => (
            <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
          ))}
        </select>
      </div>

      {/* Rationale */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Rationale <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">(optional)</span></label>
        <textarea
          value={rationale}
          onChange={e => setRationale(e.target.value)}
          rows={3}
          placeholder="Why do you hold this confidence level? What are you seeing?"
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)] resize-none"
        />
      </div>

      {/* Expected outcome */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Expected outcome <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">(optional)</span></label>
        <input
          type="text"
          value={expectedOutcome}
          onChange={e => setExpectedOutcome(e.target.value)}
          placeholder="What do you expect to happen if we proceed?"
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)]"
        />
      </div>

      {/* Timeline */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Expected timeline <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">(days, optional)</span></label>
        <input
          type="number"
          min={1}
          value={expectedDays}
          onChange={e => setExpectedDays(e.target.value)}
          placeholder="90"
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)]"
        />
      </div>

      {/* Risk flags */}
      <div>
        <label className="text-xs font-medium text-[var(--text-sec)] dark:text-[var(--text-pri)] block mb-1.5">Risk flags <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">(one per line, optional)</span></label>
        <textarea
          value={riskFlags}
          onChange={e => setRiskFlags(e.target.value)}
          rows={2}
          placeholder={"Team capacity constrained\nDependency on unconfirmed vendor"}
          className="w-full bg-[var(--bg-raised)] border border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:border-[var(--rule)] px-3 py-2 text-xs text-[var(--text-pri)] dark:text-[var(--text-pri)] placeholder:text-[var(--text-ter)] dark:placeholder:text-[var(--text-ter)] focus:outline-none focus:border-[var(--slate)] resize-none"
        />
      </div>

      {error && (
        <p className="text-xs text-[var(--coral)] bg-[var(--coral-dim)] border border-[var(--coral)] dark:text-[var(--coral)] dark:bg-[var(--coral)]/20 dark:border-[var(--coral)]/50 rounded px-3 py-2">{error}</p>
      )}

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={saving || !chosenItemId}
          className="flex-1 bg-[var(--slate)] hover:bg-[var(--slate)] disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 transition-colors"
        >
          {saving ? 'Saving…' : 'Record Signal'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-xs text-[var(--text-ter)] hover:text-[var(--text-pri)] border border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)] dark:border-[var(--rule-bright)] transition-colors"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}
